import React from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { SUPPORTED_LANGUAGES, setLocalLanguage } from '../i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Phase i18n · Global language toggle.
 *
 * Variants:
 *   - "compact"  → icon + code (fits in dense navs / mobile drawers).
 *   - "full"     → icon + full language name (public pages, settings).
 *   - "inline"   → plain button pair (no dropdown), used on landing header.
 *
 * When the user is authenticated the choice is mirrored on
 * `users.preferred_language` via `AuthContext.setLanguage`. Anonymous
 * flows persist to localStorage only.
 */
const LABELS = {
  it: 'Italiano',
  en: 'English',
};

const LanguageSwitcher = ({
  variant = 'compact',
  align = 'end',
  className = '',
  testId = 'language-switcher',
}) => {
  const { i18n, t } = useTranslation('common');
  const auth = useAuth();
  const active = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0];

  const apply = async (lang) => {
    if (lang === active) return;
    if (auth?.setLanguage) {
      await auth.setLanguage(lang);
    } else {
      setLocalLanguage(lang);
    }
  };

  if (variant === 'inline') {
    return (
      <div
        className={`inline-flex items-center gap-1 rounded-full border border-border/60 bg-card/60 p-1 ${className}`}
        data-testid={testId}
        role="group"
        aria-label={t('language')}
      >
        {SUPPORTED_LANGUAGES.map((lng) => {
          const isActive = lng === active;
          return (
            <button
              key={lng}
              type="button"
              onClick={() => apply(lng)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                isActive
                  ? 'gold-gradient text-neutral-950 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`${testId}-${lng}`}
              aria-pressed={isActive}
            >
              {LABELS[lng] || lng.toUpperCase()}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-amber-500/40 hover:text-amber-200 ${className}`}
          data-testid={testId}
          aria-label={t('language')}
        >
          <Globe className="h-4 w-4" strokeWidth={1.8} />
          {variant === 'full' ? (
            <span>{LABELS[active] || active.toUpperCase()}</span>
          ) : (
            <span className="uppercase tracking-wide">{active}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-44">
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('language')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onClick={() => apply(lng)}
            className="flex items-center justify-between text-sm"
            data-testid={`${testId}-option-${lng}`}
          >
            <span>{LABELS[lng] || lng.toUpperCase()}</span>
            {lng === active && <Check className="h-4 w-4 gold-text" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
